<template>
  <div class="ba-ranking-list"
       :class="[{ 'no-data': noData }, domain.shared]">
    <div class="table-wrap">
      <table>
        <colgroup>
          <col style="width: 20px">
          <col style="width: 108px">
          <col v-if="grouping"
               style="width: 120px">
          <col style="width: 465px">
          <col style="width: 130px" v-if="!isCustomNode">
          <template v-for="([type, field], idx) in typeList">
            <col :key="`col-${type}-${field}-${idx}`"
                 :style="{ width: colWidth[type] ? `${colWidth[type]}px` : '92px' }">
          </template>
          <col style="width: 20px">
        </colgroup>
        <thead :style="{ top: computedStickyTop + 'px' }">
          <tr>
            <th></th>
            <th class="th-rank">{{ $t('ranking.column.rank') }}</th>
            <th v-if="grouping" class="th-tag">{{ !isCustomNode ? $t('ranking.column.tag') : '' }}</th>
            <th class="th-page" :class="{custom: isCustomNode}">
              {{ !isCustomNode ? $t('ranking.column.pageData') : $t('ranking.column.customData') }}
              <em>- {{ $t('ranking.basedOn', { date: dataTimeString }) }} -</em>
            </th>
            <th class="th-heatmap" v-if="!isCustomNode">
<!--              <i class="icon icon&#45;&#45;heatmap-beta"></i>-->
              {{ $t('ranking.column.heatmap') }}
            </th>
            <template v-for="[type, field] in typeList">
              <th :key="`type-${type}-${field}`"
                  :class="[{ hover: hover.col === type,
                             active: !noData && listData.length && computedSort === type,
                             highlight: highlight === type },
                           `th-${type}`]"
                  @click="changeSort(type)"
                  @mouseenter="onHover(type, 0)"
                  @mouseleave="onHover(null, null)">
                {{ $t('ranking.column.' + type) }}
                <button class="sort"
                        :class="{ active: !noData && listData.length && computedSort === type,
                                  asc: order,
                                  desc: !order }">
                  <i class="icon icon--sort-asc"></i>
                  <i class="icon icon--sort-desc"></i>
                </button>
              </th>
            </template>
            <th></th>
          </tr>
        </thead>
        <tbody v-if="!noData">
          <tr v-for="(pageData, idx) in listData"
              :key="`page-${pageData.urlId}-${idx}-${pageData.heatmapAid}`"
              :class="{ disabled: activePage && !checkActive(pageData),
                        active: activePage && checkActive(pageData)}">
            <template v-if="isDifferentWithTop(idx)">
              <td class="gap"></td>
              <td v-if="isDifferentWithTop(idx)"
                  class="td-rank">
                <span class="td-data">
                  {{ getNumberWithOrdinal(order ? firstRank - idx : firstRank + idx) }}
                </span>
              </td>
            </template>
            <template v-else>
              <td class="gap blank"></td>
              <td class="td-rank blank">
              </td>
            </template>

            <td v-if="grouping"
                class="td-tag">
              <template v-if="pageData.tagInfo && pageData.tagInfo[group].tagId > -1">
                <span class="tag-bullet">
                  <svg xmlns="http://www.w3.org/2000/svg"
                       viewBox="0 0 12 12">
                    <circle cx="6"
                            cy="6"
                            r="6"
                            :fill="pageData.tagInfo[group].colorHex" />
                  </svg>
                </span>
                <span class="tag-name">
                  <div class="tag">{{ onlySubdomain(pageData.tagInfo[group].tagName) }}</div>
                </span>
                <ui-tooltip :class="{ short: textLength(onlySubdomain(pageData.tagInfo[group].tagName)) < 40 }"
                            arrowBottom
                            arrowCenter>
                  <span class="tag-text"
                        :style="{ width: Math.min(200, textLength(onlySubdomain(pageData.tagInfo[group].tagName)) * 5.7 + (group.grouId === 1 ? 0 : 13)) + 'px' }">
                    {{ onlySubdomain(pageData.tagInfo[group].tagName) }}
                  </span>
                </ui-tooltip>
              </template>
              <template v-else>
                <span class="tag-bullet">
                  <svg xmlns="http://www.w3.org/2000/svg"
                       viewBox="0 0 12 12">
                    <circle cx="6"
                            cy="6"
                            r="6"
                            fill="#bbbbbb" />
                  </svg>
                </span>
                <span class="tag-name">
                  <div class="tag upcoming">{{ $t('group.upcoming') }}</div>
                </span>
                <ui-tooltip class="upcoming-short"
                            arrowBottom
                            arrowCenter>
                  {{ $t('group.upcoming') }}
                </ui-tooltip>
              </template>
            </td>
            <td class="td-page">
              <div class="page-td"
                   :class="{ 'with-group': grouping }">
                <div class="favorite" v-if="!isCustomNode">
                  <ui-bookmark-button :bookmarked="pageData.bookmark"
                                      :disabled="isDemo"
                                      @click="makeFavorite(pageData)" />
                </div>
                <div class="title" v-if="isCustomNode">
                  <span class="tag-bullet" style="margin-right: 8px;">
                    <svg xmlns="http://www.w3.org/2000/svg"
                         viewBox="0 0 12 12">
                      <circle cx="6"
                              cy="6"
                              r="6"
                              :fill="pageData.customColor" />
                    </svg>
                  </span>
                  <span class="page-name">
                    <span class="before">
                        <text-highlight :searchKeyword="keyword"
                                        :textItem="pageData.customDisplayName.substr(0, pageData.customDisplayName.length - 6)"
                                        :backItem="pageData.customDisplayName.substr(-6, 6)">
                        </text-highlight>
                      </span>
                      <span class="after">
                        <text-highlight :searchKeyword="keyword"
                                        :frontItem="pageData.customDisplayName.substr(0, pageData.customDisplayName.length - 6)"
                                        :textItem="pageData.customDisplayName.substr(-6, 6)">
                        </text-highlight>
                      </span>
                  </span>
                </div>
                <div class="title" v-else>
                  <div v-if="pageData.title && pageData.title.trim()"
                       class="name">
                      <span v-if="term === 'pageTitle'" class="page-name">
                        <span v-if="journeyOptions.matchType !== 'exact'" class="category-icon small" :class="pageData.AICategory1.length !== 0 && AI_CATEGORY_SECONDARY.includes(pageData.AICategory1) ? `${pageData.AICategory1}` : 'etc'">
                          <i :class="pageData.AICategory1.length !== 0 && AI_CATEGORY_SECONDARY.includes(pageData.AICategory1) ? `icon-${pageData.AICategory1}` : 'icon-etc'"></i>
                        </span>
                        <span class="before">
                          <text-highlight :searchKeyword="keyword"
                                          :textItem="pageData.title.substr(0, pageData.title.length - 6)"
                                          :backItem="pageData.title.substr(-6, 6)">
                          </text-highlight>
                        </span>
                        <span class="after">
                          <text-highlight :searchKeyword="keyword"
                                          :frontItem="pageData.title.substr(0, pageData.title.length - 6)"
                                          :textItem="pageData.title.substr(-6, 6)">
                          </text-highlight>
                        </span>
                      </span>
                    <span class="page-name" v-else>
                      <span v-if="journeyOptions.matchType !== 'exact'" class="category-icon small" :class="pageData.AICategory1.length !== 0 && AI_CATEGORY_SECONDARY.includes(pageData.AICategory1) ? `${pageData.AICategory1}` : 'etc'">
                        <i :class="pageData.AICategory1.length !== 0 && AI_CATEGORY_SECONDARY.includes(pageData.AICategory1) ? `icon-${pageData.AICategory1}` : 'icon-etc'"></i>
                      </span>
                      <span>
                        <span class="before">
                          {{ pageData.title.substr(0, pageData.title.length - 6).replace(/ /g, '&nbsp;') }}
                        </span>
                        <span class="after">
                          {{ pageData.title.substr(-6, 6).replace(/ /g, '&nbsp;') }}
                        </span>
                      </span>
                    </span>
                  </div>
                  <div v-else class="name">
                    <span class="page-name">
                      <i v-if="journeyOptions.matchType !== 'exact'" class="category-icon icon-etc"></i>
                      <span class="no-title">
                        No page title
                      </span>
                    </span>
                  </div>
                  <div class="url">
                    <a :href="pageData.listUrl"
                       :title="pageData.listUrl"
                       target="_blank">
                      <i class="icon icon--ba-link"></i>
                      <span v-if="term === 'url'">
                        <span class="before">
                          <text-highlight :searchKeyword="keyword"
                                          :textItem="pageData.listUrl.substr(0, pageData.listUrl.length - 6)"
                                          :backItem="pageData.listUrl.substr(-6, 6)">
                          </text-highlight>
                        </span>
                        <span class="after">
                          <text-highlight :searchKeyword="keyword"
                                          :frontItem="pageData.listUrl.substr(0, pageData.listUrl.length - 6)"
                                          :textItem="pageData.listUrl.substr(-6, 6)">
                          </text-highlight>
                        </span>
                      </span>
                      <span v-else>
                        <span class="before"
                              :style="{ 'max-width': `calc(100% - ${characterWidth(pageData.listUrl.substr(-6, 6)) * 6 + 30}px)` }">
                          {{ pageData.listUrl.substr(0, pageData.listUrl.length - 6) }}</span>
                        <span class="after" :style="{ width: `${characterWidth(pageData.listUrl.substr(-6, 6)) * 6 + 10}px` }">
                          {{ pageData.listUrl.substr(-6, 6) }}
                        </span>
                      </span>
                    </a>
                  </div>
                </div>
                <div class="buttons">
                  <button @click="showDetail(pageData)">
                    <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-width="2" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round" d="m11.293 14.95 4.95-4.95-4.95-4.95M16.243 10H3.5"/>
                    </svg>
                  </button>
                </div>
              </div>
            </td>
            <td class="td-heatmap" v-if="!isCustomNode" :class="{'disabled': isHeatmapDisabled}">
                <ui-heatmap-state-icon
                  :idx="idx"
                  :pageData="pageData"
                  :disabled="isOldCode"
                  :paused="isHeatmapDisabled"
                  @selectNode="selectNode = $event"
                  @selectedState="selectedState = $event"
                  @isHeatmapRun="isHeatmapRun = $event"
                  @isHeatmapStop="isHeatmapStop = $event"
                  @isHeatmapSet="isHeatmapSet"
                  @onHeatmapRegister="onHeatmapRegister"
                />
            </td>
            <template v-for="[type, field] in typeList">
              <td :key="`page-${pageData.urlId}-${idx}-${type}-${pageData.heatmapAid}`"
                  :class="`td-${type}`">
                <span v-if="type === 'pv'" class="td-data">
                  <span  v-if="pageData.countIsInaccurate" v-btf-tooltip:top.start="{contents: $t('common.invalidValueDesc'),size: '300px'}">
                    {{$t('common.invalidValue')}}
                  </span>
                  <span  v-else>
                    {{ pageData[field] | letterThousandMillion(0) }}
                  </span>
                </span>
                <span v-else-if="['stayInCount', 'dropOff', 'rollback', 'reloadCount'].includes(type)"
                      class="td-data">
                  {{ getPercentage(pageData[field], pageData.count) | fixedInt(type === 'dropOff' || type === 'stayInCount' ? 2 : 1 , 1) }}%
                  <i :class="`icon icon--ranking-${type}`"></i>
                </span>
                <span v-else-if="type === 'avgStay'"
                      class="td-data">
                  {{ getTimeString(pageData[field]) }}
                  <i :class="`icon icon--ranking-${type}`"></i>
                </span>
                <span v-else
                      v-html="pageData[type]"
                      class="td-data">
                </span>
              </td>
            </template>
            <td class="gap"></td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="noData"
         class="no-data">
      <i class="icon loading--empty"></i>
      <p class="no-data-title">
        {{ $t('common.noResult') }}
      </p>
      <template v-if="keyword">
        <p class="no-data-description">
          {{ $t('common.noResultSearch') }}
        </p>
        <p class="no-data-keyword">
          '{{ keyword }}'
        </p>
      </template>
    </div>

    <dimmed v-if="isHeatmapRun">
      <alert
          :icon="'icon--heatmap-confirm'"
          :title="$t('heatmap.confirm.run.title')"
          :description="selectedState !== 'stop' ? $t('heatmap.confirm.run.description') : ''"
          :submit="$t('common.confirm')"
          :cancel="$t('common.cancel')"
          @onCancel="isHeatmapRun = false;selectNode = null"
          @onSubmit="stateUpdate(true)" />
    </dimmed>

    <dimmed v-if="isHeatmapStop">
      <alert
        :icon="'icon--heatmap-confirm'"
        :title="$t('heatmap.confirm.stop.title')"
        :description="$t('heatmap.confirm.stop.description')"
        :submit="$t('common.confirm')"
        :cancel="$t('common.cancel')"
        @onCancel="isHeatmapStop = false;selectNode = null"
        @onSubmit="stateUpdate(false)" />
    </dimmed>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getPercentage, getTimeString, getNumberWithOrdinal, characterWidth, onlySubdomain } from '@/libs/utils'

import TextHighlight from '@/components/ui/TextHighlight'
import UiTooltip from '@/components/ui/UiTooltip'
import UiBookmarkButton from '@/components/ui/UiBookmarkButton'
import UiHeatmapStateIcon from '../ui/UiHeatmapStateIcon'
import { HEATMAP_STATE, AI_CATEGORY_SECONDARY } from '@/libs/constants'
import Alert from '@/components/ui/Alert.vue'
import Dimmed from '@/components/ui/Dimmed.vue'
import { updateBaApply } from '@/api/heatmap'

export default {
  name: 'BaRankingList',
  components: {
    Dimmed,
    // UiIconTooltip,
    Alert,
    UiTooltip,
    UiBookmarkButton,
    UiHeatmapStateIcon,
    TextHighlight
  },
  computed: {
    ...mapState([
      'journeyOptions',
      'numBookmarks',
      'domain',
      'isSampling',
      'isDemo'
    ]),
    isCustomNode () {
      return this.journeyOptions.isCustomNode
    },
    isOldCode () {
      return this.domain.v1LastFoundDate && !this.domain.v2LastFoundDate
    },
    isHeatmapDisabled () {
      return this.domain.shared === 'shared' || this.isDemo
    },
    typeList () {
      return Object.entries(this.types)
    },
    order () {
      return !this.sort.includes('-')
    },
    firstRank () {
      return this.order ? this.totalCount : 1
    },
    computedSort () {
      let sortKey = this.sort
      if (sortKey.indexOf('resampleCount') > -1) {
        sortKey = sortKey.replace('resampleCount', 'pv')
      }
      return sortKey.replace('-', '')
    },
    computedStickyTop () {
      if (this.keyword) {
        return 125
      }
      return 72
    },
    AI_CATEGORY_SECONDARY () {
      return AI_CATEGORY_SECONDARY
    }
  },
  methods: {
    getEndByTooltip (type) {
      let result
      if (type === 'stopByDomainCreditEmpty') {
        result = this.$t('heatmap.registration.tooltip.stopByDomainCreditEmpty')
      } else if (type === 'stopByUserCreditEmpty') {
        result = this.$t('heatmap.registration.tooltip.stopByUserCreditEmpty')
      } else if (type === 'stopByJamPvEmpty') {
        result = this.$t('heatmap.registration.tooltip.stopByJamPvEmpty')
      }
      return result
    },
    textLength (string) {
      return string.replace(/[0-\x7f]|([0-\u07ff]|(.))/g, '$&$1$2').length
    },
    onlySubdomain (tag) {
      return onlySubdomain(tag, this.journeyOptions.domain)
    },
    getPercentage (value, total) {
      return getPercentage(value, total)
    },
    getTimeString (value) {
      return getTimeString(value / 1000)
    },
    getNumberWithOrdinal (value) {
      return getNumberWithOrdinal(value)
    },
    isDifferentWithTop (idx) {
      if (idx < 1) {
        return true
      }
      let value0 = this.listData[idx][this.types[this.computedSort]]
      let value1 = this.listData[idx - 1][this.types[this.computedSort]]
      if (['stayInCount', 'dropOff', 'rollback', 'reloadCount'].includes(this.computedSort)) {
        value0 = getPercentage(value0, this.listData[idx].count)
        value1 = getPercentage(value1, this.listData[idx - 1].count)
      } else if (this.computedSort === 'avgStay') {
        value0 = Math.round(value0 / 1000)
        value1 = Math.round(value1 / 1000)
      } else {
        const currentCount = this.isSampling ? this.listData[idx].resampleCount : this.listData[idx].count
        const prevCount = this.isSampling ? this.listData[idx - 1].resampleCount : this.listData[idx - 1].count
        value0 = currentCount
        value1 = prevCount
      }
      return value0 !== value1
    },
    onHover (col, row) {
      this.hover.col = col
      this.hover.row = row
    },
    insertPage (pageData) {
      this.$emit('insertPage', pageData)
    },
    showDetail (pageData) {
      this.$emit('showDetail', pageData)
    },
    makeFavorite (pageData) {
      this.$emit('makeFavorite', pageData)
    },
    changeSort (type) {
      this.$emit('changeSort', type)
    },
    characterWidth (value) {
      return characterWidth(value)
    },
    isHeatmapSet () {
      this.onHeatmapRegister(this.selectNode.data, this.selectNode.index)
    },
    stateUpdate (run) {
      const baApply = this.getTargetNode(this.selectNode.index).baApply
      if (baApply) {
        if (baApply.state === 'endByAnalysisDate') {
          this.onHeatmapRegister(this.selectNode.data, this.selectNode.index)
        } else {
          const baAid = baApply._id
          if (run && (baApply.state === 'stop')) {
            updateBaApply({ id: baAid, parentSid: baApply.parentSid, sid: baApply.sid, state: 'run' }, true).then((resp) => {
              this.isHeatmapRun = false
              this.$emit('changeState')
            })
          } else if (!run && (baApply.state === 'run' || baApply.uiState === 'error' || baApply.state === 'stopByDomainCreditEmpty' || baApply.state === 'stopByUserCreditEmpty' || baApply.state === 'stopByJamPvEmpty')) {
            updateBaApply({ id: baAid, state: 'stop' }, true).then((resp) => {
              this.isHeatmapStop = false
              this.$emit('changeState')
            })
          }
        }
      } else {
        this.onHeatmapRegister(this.selectNode.data, this.selectNode.index)
      }
    },
    onHeatmapRegister (item, index) {
      this.selectNode = {
        data: item,
        index: index
      }
      const targetNode = this.getTargetNode(index)
      // targetNode.heatmapState = HEATMAP_STATE.RUN
      this.isHeatmapRun = false
      this.isHeatmapStop = false

      this.$emit('onHeatmapRegistration', targetNode)
    },
    onHeatmapStop () {
      // api stop
      const targetNode = this.getTargetNode(this.selectNode.index)
      targetNode.heatmapState = HEATMAP_STATE.STOP
      this.isHeatmapStop = false
    },
    getTargetNode (index) {
      if (index === null) {
        return null
      }
      return this.listData[this.selectNode.index]
    },
    checkActive (pageData) {
      if (this.journeyOptions.matchType === 'exact') {
        return this.activePage.url === pageData.url
      }
      return this.activePage.urlId === pageData.urlId && this.activePage.subDomSid === pageData.subDomSid
    }
  },
  props: {
    totalCount: {
      type: Number,
      default: 0
    },
    listData: {
      type: Array,
      default: () => []
    },
    sort: {
      type: String,
      default: ''
    },
    highlight: {
      default: null
    },
    activePage: {
      default: null
    },
    dataTimeString: {
      type: String,
      default: ''
    },
    term: {
      default: null
    },
    keyword: {
      default: null
    },
    noData: {
      type: Boolean,
      default: false
    },
    grouping: {
      type: Boolean,
      default: false
    },
    group: {
      default: {}
    }
  },
  created () {
    this.rank = this.firstRank
  },
  watch: {
    firstRank (value) {
      this.rank = value
    }
  },
  data () {
    return {
      showText: false,
      selectedState: null,
      testbtn: false,
      rank: 1,
      hover: {
        col: null,
        row: null
      },

      types: {
        pv: 'count',
        stayInCount: 'stayInCount',
        dropOff: 'dropOff',
        rollback: 'rollback',
        reloadCount: 'reloadCount',
        avgStay: 'avgStay'
      },

      colWidth: {
        'pv': 112,
        'avgStay': 112,
        'heatmap': 130
      },
      isHeatmapRun: false,
      isHeatmapStop: false,
      selectNode: null
    }
  }
}
</script>

<style lang="scss" scoped>
.ba-ranking-list {
  position: relative;
  height: 100%;
  border-radius: 6px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
  background-color: #fff;
  &.shared {
    .favorite {
      pointer-events: none;
      opacity: .5;
      .bookmark-button /deep/.button {
        box-shadow: none;
      }
    }
  }
  &.no-data {
    box-shadow: none;
    background-color: transparent;
  }

  .no-data {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;

    .no-data-title {
      font-size: 20px;
      font-weight: 700;
      line-height: 1;
      color: #2f2f2f;
      margin: 10px 0 15px;
    }

    .no-data-description {
      font-size: 14px;
      color: #767676;
    }

    .no-data-keyword {
      margin-top: 30px;
      font-size: 15px;
      font-weight: 500;
      color: #ff8400;
    }
  }

  .table-wrap {
    position: relative;
    height: calc(100% - 1px);

    table {
      width: 100%;

      thead {
        z-index: 8;
        position: sticky;
        transition: top 0.3s cubic-bezier(.28,.12,.42,.94);
        width: 100%;

        &::before {
          content: "";
          z-index: -1;
          position: absolute;
          top: 0;
          left: 50%;
          width: 100%;
          height: 55px;
          background: #333333;
          transform: translateX(-50%) translateZ(0);
          border-radius: 6px 6px 0 0;
          transition: width 0.1s cubic-bezier(.28,.12,.42,.94);
        }

        tr {
          z-index: 2;
          position: relative;

          th {
            position: relative;
            height: 36px;
            font-size: 13px;
            font-weight: normal;
            background: #333333;
            color: #ffffff;
            padding: 8px 9px 11px 12px;
            vertical-align: bottom;
            text-align: right;
            line-height: 20px;
            transition: background-color 0.15s ease;
            white-space: nowrap;

            .sort {
              display: inline-block;
              width: 14px;
              height: 18px;
              margin-top: -2px;
              margin-left: 2px;
              vertical-align: middle;
              border-radius: 4px;
              transition: background-color 0.15s ease,
                          box-shadow 0.15s ease;

              i.icon {
                float: left;
                opacity: 0.2;
                transition: opacity 0.15s ease;
              }

              &.active {
                &.asc {
                  .icon--sort-asc {
                    opacity: 1;
                  }
                }

                &.desc {
                  .icon--sort-desc {
                    opacity: 1;
                  }
                }
              }

              &.active {
                background-color: #272727;
                box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.146);
              }
            }

            &.hover {
              .sort {
                background-color: #272727;
                box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.146);
              }
            }

            &.th-heatmap {
              position: relative;
              width: 142px;
              padding-right: 28px;
              & .icon--heatmap-beta {
                display: inline-block;
                position: absolute;
                top: calc(50% - 7px);
                right: 36px;
                transform: translate(0, -50%);
              }
            }

            &.th-tag {
              text-align: center;
              width: 130px;

              @media screen and (max-width: 1240px) {
                width: 40px;
              }
            }

            &.th-rank {
              text-align: center;
              padding-left: 8px;
              padding-right: 8px;
            }

            &.th-page {
              text-align: left;
              padding-left: 44px;

              &.custom {
                padding-left: 0;
              }

              em {
                margin-left: 12px;
                font-size: 12px;
                color: #ffee00;
              }
            }

            &.th-pv.active {
              background-color: #555555;
            }

            &.th-stayInCount.active {
              background-color: #81a507;
            }

            &.th-dropOff.active {
              background-color: #ff4a00;
            }

            &.th-rollback.active {
              background-color: #9665e3;
            }

            &.th-reloadCount.active {
              background-color: #0074ff;
            }

            &.th-avgStay.active {
              background-color: #4b8f0d;
            }

            &.th-pv.hover {
              cursor: pointer;
              background-color: #444444;
            }

            &.th-stayInCount.hover {
              cursor: pointer;
              background-color: #6a8314;
            }

            &.th-dropOff.hover {
              cursor: pointer;
              background-color: #c1430f;
            }

            &.th-rollback.hover {
              cursor: pointer;
              background-color: #7856ad;
            }

            &.th-reloadCount.hover {
              cursor: pointer;
              background-color: #1060c1;
            }

            &.th-avgStay.hover {
              cursor: pointer;
              background-color: #447317;
            }

            &:first-child {
              border-top-left-radius: 7px;
            }

            &:last-child {
              border-top-right-radius: 7px;
            }
          }
        }
      }

      tbody {
        tr {
          transition: opacity 0.3s ease,
                      box-shadow 0.3s ease;

          td {
            height: 88px;
            padding-left: 0;
            border-top: 1px solid #ebebeb;
            text-align: right;
            color: #666666;
            font-size: 14px;
            line-height: 1.46;
            white-space: nowrap;
            transition: border-top-color 0.1s ease;

            .td-data {
              display: inline-block;
              min-width: 76px;
              padding-right: 12px;
            }

            &.td-tag {
              position: relative;
              width: 130px;
              vertical-align: middle;
              text-align: left;
              transition: width 0.2s ease,
                          opacity 0.2s ease;

              @media screen and (max-width: 1280px) {
                text-align: center;
              }

              .tag-bullet {
                display: inline-block;
                vertical-align: middle;
                width: 12px;
                height: 12px;

                svg {
                  display: inline-block;
                  vertical-align: top;
                  width: 12px;
                  height: 12px;
                  margin-top: -1px;

                  circle {
                    stroke: rgba(0, 0, 0, 0.05);
                    stroke-width: 1;
                  }
                }

                .icon {
                  vertical-align: middle;
                  margin: -11px 0 0 0;
                }
              }

              .tag-name {
                display: inline-block;
                vertical-align: middle;
                padding-left: 8px;
                font-size: 12px;
                line-height: 1.33;
                color: #666;
                overflow: hidden;
                width: 83px;
                transition: width 0.2s ease,
                            opacity 0.2s ease,
                            padding-left 0.2s ease;

                @media screen and (max-width: 1280px) {
                  width: 0;
                  opacity: 0;
                  padding-left: 0;
                }

                .tag {
                  text-overflow: ellipsis;
                  display: -webkit-box;
                  -webkit-line-clamp: 3;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                  white-space: normal;
                  word-break: break-all;

                  &.upcoming {
                    word-break: break-word;
                    font-weight: 500;
                  }
                }
              }

              .bubble {
                z-index: 14;
                position: absolute;
                top: calc(50% - 50px);
                left: calc(50% - 1px);
                transform: translate(-50%, -50%);
                transition: opacity 0.15s ease;
                opacity: 0;
                pointer-events: none;

                @media screen and (max-width: 1240px) {
                  transition: translate(-50%, 0);
                }

                &.upcoming-short,
                &.short {
                  @media screen and (min-width: 1440px) {
                    display: none;
                  }
                }

                & /deep/ .tag-text {
                  display: inline-block;
                  width: 200px;
                  white-space: normal;
                  word-break: break-all;

                  @media screen and (max-width: 1240px) {
                    max-width: 145px;
                  }
                }
              }

              &:hover {
                .bubble {
                  opacity: 1;
                }
              }
            }

            &.td-pv {
              height: 85px;
              padding-bottom: 3px;

              .td-data {
                // min-width: 100px;
              }
            }

            &.td-heatmap {
              position: relative;
              &.disabled {
                pointer-events: none;
              }
              .td-data {
                .icon--heatmap-state {
                  position: relative;
                  padding-right: 50px;
                }
                .icon--heatmap-setting {
                  position: absolute;
                  top: 50%;
                  transform: translate(0, -50%);
                  right: 20px;
                }
              }
            }

            &.td-avgStay {
              .td-data {
                min-width: 100px;
              }
            }

            &.gap {
              &.blank {
                border-top: none;
              }
            }

            &.td-rank {
              text-align: center;
              font-size: 13px;
              padding-left: 12px;

              .td-data {
                min-width: 0;
                line-height: 88.5px;
                position: sticky;
                top: 126px;
              }

              &.blank {
                border-top: none;
              }
            }

            &.td-page {
              text-align: left;
              padding-left: 0;

              .page-td {
                display: flex;
                align-items: center;
                max-width: 550px;

                @media screen and (max-width: 1600px) {
                  max-width: calc(100vw - 890px);

                  &.with-group {
                    max-width: calc(100vw - 970px);
                  }
                }

                @media screen and (max-width: 1240px) {
                  max-width: 360px;

                  &.with-group {
                    max-width: 340px;
                  }
                }

                .favorite {
                  flex-basis: 39px;
                  flex-grow: 0;
                  flex-shrink: 0;
                  text-align: center;

                  .bookmark-button {
                    &:disabled /deep/ .button {
                      background-color: #f4f4f4;
                      opacity: 0.6;
                    }

                    &:hover {
                      & /deep/ .tooltip {
                        transform: translate3d(0, calc(-100% - 5px - 3px), 0);
                      }
                    }

                    & /deep/ .tooltip {
                      left: -5px;
                      right: auto;
                      top: 0;
                      transform: translate3d(0, calc(-100% - 5px - 3px + 10px), 0);

                      &:after {
                        left: calc(20px - 3.5px);
                        top: auto;
                        bottom: -3px;
                        transform: rotate(135deg);
                      }
                    }
                  }
                }

                .title {
                  flex-grow: 1;
                  flex-shrink: 1;
                  min-width: 0;
                  padding-left: 5px;

                  .tag-bullet {
                    display: inline-block;
                    vertical-align: middle;
                    width: 12px;
                    height: 12px;

                    svg {
                      display: inline-block;
                      vertical-align: top;
                      width: 12px;
                      height: 12px;
                      margin-top: -1px;

                      circle {
                        stroke: rgba(0, 0, 0, 0.05);
                        stroke-width: 1;
                      }
                    }

                    .icon {
                      vertical-align: middle;
                      margin: -11px 0 0 0;
                    }
                  }

                  .name {
                    color: #444444;
                    padding-left: 5px;
                    margin-bottom: 4px;
                    margin-right: 40px;

                    .page-name {
                      .category-icon {
                        margin-right: 9px;
                      }

                      span {
                        vertical-align: middle;
                        margin-top: 1px;
                      }

                      .before {
                        display: inline-block;
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                        max-width: calc(100% - 60px);
                        min-width: 0;
                        vertical-align: top;
                      }

                      .after {
                        display: inline-block;
                        width: 35px;
                        vertical-align: top;
                      }

                      .no-title {
                        vertical-align: top;
                        font-style: italic;
                        color: #888888;
                      }
                    }
                  }

                  .url {
                    .before {
                      display: inline-block;
                      overflow: hidden;
                      white-space: nowrap;
                      text-overflow: ellipsis;
                      max-width: calc(100% - 70px);
                      min-width: 0;
                      vertical-align: middle;
                      padding-bottom: 2px;
                    }

                    .after {
                      display: inline-block;
                      width: 50px;
                      vertical-align: middle;
                      padding-bottom: 2px;
                    }

                    a {
                      font-size: 12px;
                      line-height: 1;
                      color: #888888;
                    }

                    i.icon {
                      margin-right: 2px;
                    }
                  }
                }
              }

              .buttons {
                text-align: center;
                margin-left: 10px;

                button {
                  position: relative;
                  width: 32px;
                  height: 32px;
                  border-radius: 5px;
                  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.06);
                  background-color: #fff;
                  color: #444444;
                  transition: background-color 0.2s ease,
                              box-shadow 0.2s ease,
                              color 0.3s ease;

                  svg {
                    position: absolute;
                    top: 6px;
                    right: 6px;
                    width: 20px;
                    height: 20px;

                    path {
                      stroke: #666666;
                      transition: stroke 0.2s ease;
                    }
                  }

                  &:hover {
                    background-color: #efefef;
                  }

                  &:active {
                    background-image: linear-gradient(to top, #888888 0%, #666666 100%);
                    box-shadow: inset 0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.06);
                    color: #ffffff;

                    svg {
                      path {
                        stroke: #ffffff;
                        transition: stroke 0.2s ease;
                      }
                    }
                  }

                  i.icon {
                    vertical-align: middle;
                    margin-right: 4px;
                  }
                }
              }
            }

            i.icon {
              margin: -2px -4px 0 4px;
              vertical-align: middle;
            }
          }

          &:first-child {
            td {
              border-top: none;
            }
          }

          &.disabled {
            opacity: 0.5;
          }

          &.active {
            box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 15px 30px -5px rgba(0, 0, 0, 0.25), 0 0 0 3px #333333;

            td {
              border-top-color: transparent;
            }
          }
        }

        tr.active + tr {
          td {
            border-top-color: transparent;
          }
        }
      }
    }
  }
}
</style>
